<script>
  import { createEventDispatcher } from 'svelte';
  import { worker } from '../../stores/worker';

  export let worker_data = null;

  const dispatch = createEventDispatcher();

  let formData = {
    name: worker_data?.name || '',
    email: worker_data?.email || '',
    phone: worker_data?.phone || '',
    title: worker_data?.title || '',
    status: worker_data?.status || 'AVAILABLE',
    skills: worker_data?.skills || []
  };

  let loading = false;
  let error = '';
  let newSkill = '';

  const statusOptions = [
    { value: 'AVAILABLE', label: 'Available' },
    { value: 'BUSY', label: 'Busy' },
    { value: 'ON_LEAVE', label: 'On Leave' },
    { value: 'INACTIVE', label: 'Inactive' }
  ];

  function formatPhone(v) {
    const d = v.replace(/\D/g, '');
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
  }

  function addSkill() {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      formData.skills = [...formData.skills, newSkill.trim()];
      newSkill = '';
    }
  }

  function removeSkill(i) {
    formData.skills = formData.skills.filter((_, idx) => idx !== i);
  }

  async function handleSubmit() {
    if (!formData.name.trim()) {
      error = 'Name is required';
      return;
    }

    loading = true;
    error = '';

    try {
      const data = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.replace(/\D/g, '') || null,
        title: formData.title.trim() || null,
        status: formData.status,
        skills: formData.skills
      };

      if (worker_data?.id) {
        await worker.updateWorker(worker_data.id, data);
      } else {
        await worker.createWorker(data);
      }
      dispatch('close');
    } catch (e) {
      error = e.message || 'Failed to save';
    } finally {
      loading = false;
    }
  }
</script>

<div class="form-container">
  <div class="form-header">
    <h2>{worker_data ? 'Edit Worker' : 'New Worker'}</h2>
    <button class="btn btn-secondary" on:click={() => dispatch('close')}>Cancel</button>
  </div>

  {#if error}
    <div class="form-error">{error}</div>
  {/if}

  <form on:submit|preventDefault={handleSubmit}>
    <div class="form-group">
      <label for="name">Name <span class="required">*</span></label>
      <input id="name" type="text" bind:value={formData.name} placeholder="Worker name" required />
    </div>

    <div class="form-group">
      <label for="title">Job Title</label>
      <input id="title" type="text" bind:value={formData.title} placeholder="e.g. Electrician" />
    </div>

    <div class="form-row">
      <div class="form-group">
        <label for="email">Email</label>
        <input id="email" type="email" bind:value={formData.email} placeholder="email@example.com" />
      </div>
      <div class="form-group">
        <label for="phone">Phone</label>
        <input id="phone" type="tel" value={formData.phone} on:input={e => formData.phone = formatPhone(e.target.value)} placeholder="(555) 555-5555" />
      </div>
    </div>

    <div class="form-group">
      <label for="status">Status</label>
      <select id="status" bind:value={formData.status}>
        {#each statusOptions as opt}
          <option value={opt.value}>{opt.label}</option>
        {/each}
      </select>
    </div>

    <div class="form-group">
      <label>Skills</label>
      <div class="skills-input">
        <input type="text" bind:value={newSkill} placeholder="Add skill..." on:keydown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())} />
        <button type="button" class="btn btn-secondary" on:click={addSkill}>Add</button>
      </div>
      {#if formData.skills.length > 0}
        <div class="skills-list">
          {#each formData.skills as skill, i}
            <span class="skill-tag">
              {skill}
              <button type="button" on:click={() => removeSkill(i)}>&times;</button>
            </span>
          {/each}
        </div>
      {/if}
    </div>

    <div class="form-actions">
      <button type="submit" class="btn btn-primary" disabled={loading}>
        {loading ? 'Saving...' : 'Save Worker'}
      </button>
    </div>
  </form>
</div>

<style>
  .form-container {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .form-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
  }

  .form-header h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .form-error {
    margin: 16px 20px 0;
    padding: 12px;
    background: #fee2e2;
    color: #dc2626;
    border-radius: var(--radius);
    font-size: 13px;
  }

  form {
    padding: 20px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 6px;
    color: var(--text);
  }

  .required {
    color: var(--danger);
  }

  input, select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 14px;
    background: #fff;
  }

  input:focus, select:focus {
    outline: none;
    border-color: var(--accent);
  }

  .skills-input {
    display: flex;
    gap: 8px;
  }

  .skills-input input {
    flex: 1;
  }

  .skills-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }

  .skill-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: var(--bg);
    border-radius: 4px;
    font-size: 12px;
  }

  .skill-tag button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-muted);
    font-size: 14px;
    padding: 0;
    line-height: 1;
  }

  .skill-tag button:hover {
    color: var(--danger);
  }

  .form-actions {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }

  @media (max-width: 640px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }
</style>
